import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExamContext } from '../contexts/ExamContext';
import Timer from './Timer';
import { FaLanguage } from 'react-icons/fa';
import { getOptionKey } from '../utils/helpers';

const Question = () => {
  const {
    selectedSet,
    questions,
    answers,
    setAnswers,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    language,
    setLanguage,
    handleSubmit,
    isSubmitted,
    resetExamState
  } = useContext(ExamContext);

  const navigate = useNavigate();

  // State for go-to input
  const [goToValue, setGoToValue] = useState('');
  const [goToError, setGoToError] = useState('');

  // Defensive: Set q to null if questions array is empty or currentQuestionIndex out of range
  const q =
    questions && questions.length > 0 && currentQuestionIndex < questions.length
      ? questions[currentQuestionIndex]
      : null;

  // Redirect to home if no selectedSet (to prevent invalid direct access)
  useEffect(() => {
    if (!selectedSet) {
      navigate('/', { replace: true });
    }
  }, [selectedSet, navigate]);

  useEffect(() => {
    // Warn on refresh if test in progress
    const handleBeforeUnload = (e) => {
      if (!isSubmitted) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Clean up listener on unmount or when test submitted
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isSubmitted]);

  // Auto-navigate to results when submitted (manual or auto)
  useEffect(() => {
    if (isSubmitted) {
      navigate('/results');
    }
  }, [isSubmitted, navigate]);

  // Clear go-to input when question changes (via any navigation method)
  useEffect(() => {
    setGoToValue('');
    setGoToError('');
  }, [currentQuestionIndex]);

  // Scroll to top when question changes (especially helpful on mobile)
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [currentQuestionIndex]);

  if (!q) return <p>Loading questions...</p>;

  // ---- Helper: normalize keys like "A", "a", " i ", "iv" ----
  const normalizeKey = (key) =>
    (key || '').toString().trim().toLowerCase();

  const normalizedCorrect = normalizeKey(q.correct);

  const userAnswer = answers[q.id];
  const answered = userAnswer !== undefined && userAnswer !== null;
  const normalizedUserAnswer = normalizeKey(userAnswer);

  const isCorrectAnswer =
    answered && normalizedUserAnswer === normalizedCorrect;

  // Determine button class names for options based on answer state
  const getOptionClass = (optionText) => {
    let className = 'option-button';

    if (!answered) {
      // No answer selected yet, normal state
      return className;
    }

    // Add 'answered' class to disable hover effects
    className += ' answered';

    const optionKey = getOptionKey(optionText);

    if (optionKey === normalizedCorrect) {
      // Always highlight correct option with green background
      className += ' correct-option';
    }

    if (optionKey === normalizedUserAnswer && !isCorrectAnswer) {
      // User selected wrong option, highlight in red
      className += ' wrong-selected';
    }

    if (optionKey === normalizedUserAnswer && isCorrectAnswer) {
      // User selected correct option, highlight in green
      className += ' correct-selected';
    }

    return className;
  };

  // Handler for option select, only when question not answered yet
  const handleOptionClick = (optionText) => {
    if (answered) return; // prevent changing selection once answered

    const optionKey = getOptionKey(optionText); // store normalized key like "a", "d", "i", "iv"
    setAnswers({
      ...answers,
      [q.id]: optionKey
    });
  };

  // for bold roman options:
  const formatQuestionText = (text) => {
    return text
      .replace(/I\./g, '<strong>I.</strong>')
      .replace(/II\./g, '<strong>II.</strong>')
      .replace(/III\./g, '<strong>III.</strong>')
      .replace(/IV\./g, '<strong>IV.</strong>');
  };

  // Handler for go-to functionality
  const handleGoTo = () => {
    const trimmedValue = goToValue.trim();

    // Validation 1: Check if input is empty
    if (!trimmedValue) {
      setGoToError('Please enter a question number.');
      return;
    }

    // Validation 3: Check if input is a valid number
    const num = parseInt(trimmedValue, 10);
    if (isNaN(num)) {
      setGoToError('Please enter a valid number.');
      return;
    }

    // Validation 2: Check if number is within range (1 to questions.length)
    if (num < 1 || num > questions.length) {
      setGoToError(`Question number must be between 1 and ${questions.length}.`);
      return;
    }

    // If all validations pass, navigate to the question (0-based index)
    setCurrentQuestionIndex(num - 1);
    setGoToValue('');
    setGoToError('');
  };

  return (
    <div className="exam-container">
      <Timer />
      <div className="content-area">
        <div className="language-toggle">
          <button onClick={() => setLanguage(language === 'en' ? 'hi' : 'en')}>
            <FaLanguage /> Switch to {language === 'en' ? 'Hindi' : 'English'}
          </button>
          <button
            className="go-home-btn"
            onClick={() => {
              if (window.confirm('Are you sure you want to go to Home Page?')) {
                resetExamState();
                navigate('/');
              }
            }}
            aria-label="Go to Home"
          >
            Go to Home
          </button>
        </div>

        <div className="question-display">
          <h3>
            Question {currentQuestionIndex + 1} of {questions.length}
          </h3>

          <p
            className="question-text"
            style={{ whiteSpace: 'pre-line' }}
            dangerouslySetInnerHTML={{
              __html: formatQuestionText(
                language === 'en' ? q.question_en : q.question_hi
              )
            }}
          ></p>

          <div className="options-grid">
            {q.options.map((opt, index) => (
              <button
                key={index}
                className={getOptionClass(opt)}
                onClick={() => handleOptionClick(opt)}
                disabled={answered}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div className="exam-navigation-container">
          <button
            onClick={() =>
              setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))
            }
            disabled={currentQuestionIndex === 0}
            className="nav-btn"
          >
            Previous
          </button>

          <div className="go-to-container">
            <input
              type="text"
              value={goToValue}
              onChange={(e) => setGoToValue(e.target.value)}
              placeholder="Go to Q#"
              className="go-to-input"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleGoTo();
                }
              }}
            />
            <button onClick={handleGoTo} className="go-to-btn">
              Go
            </button>
            {goToError && <div className="go-to-error">{goToError}</div>}
          </div>

          <button
            onClick={() =>
              setCurrentQuestionIndex(
                Math.min(questions.length - 1, currentQuestionIndex + 1)
              )
            }
            disabled={currentQuestionIndex === questions.length - 1}
            className="nav-btn"
          >
            Next
          </button>
        </div>

        <div className="submit-btn-container">
          <button
            className="submit-btn"
            onClick={() => {
              if (window.confirm('Are you sure you want to submit? YES/NO')) {
                handleSubmit();
              }
            }}
          >
            Submit Test
          </button>
        </div>
      </div>
    </div>
  );
};

export default Question;
